import { toast } from "sonner";

// Shared audio alerts for the batching sequence. Browsers suspend an AudioContext
// until the tab has been clicked, and they hand back a DIFFERENT context each time
// you construct one — so we keep a single context and resume it before every tone.
// Not doing that is why the plant heard nothing.
let ctx = null;
function audio() {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  if (!ctx) ctx = new Ctx();
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  return ctx;
}

export function tone(freq, dur = 0.2, delay = 0, vol = 0.4) {
  try {
    const c = audio();
    if (!c) return;
    const t0 = c.currentTime + delay;
    const o = c.createOscillator();
    const g = c.createGain();
    o.type = "square";
    o.frequency.value = freq;
    o.connect(g);
    g.connect(c.destination);
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(vol, t0 + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    o.start(t0);
    o.stop(t0 + dur + 0.02);
  } catch { /* audio blocked until the operator clicks once */ }
}

// Driver "new load" bell — a bright doorbell-style two-note chime plus a phone
// buzz, so a load pinged to the cab is impossible to miss. Called on a repeat
// timer by the Driver App until the driver acknowledges.
export function newLoadBell() {
  tone(1318, 0.45, 0, 0.6);     // E6
  tone(1047, 0.6, 0.22, 0.6);   // C6
  tone(1568, 0.5, 0.5, 0.55);   // G6
  try { navigator.vibrate?.([350, 140, 350, 140, 600]); } catch { /* unsupported */ }
}

// ---- Voice personas -------------------------------------------------------------
// Browser TTS only has the voices the PC has installed, so each persona lists preferred
// languages in order and the phrase to use for each. Punjabi falls back to Hindi
// (Gurmukhi -> Devanagari) and then to romanised text on an Indian-English voice.
export const VOICE_PERSONAS = [
  { id: "standard", label: "Standard", langs: ["en-AU", "en-GB", "en-US", "en"],
    phrases: { loadTruck: "Load Truck", loadComplete: "Load Complete", test: "Hello. Sound is on." }, rate: 0.95, pitch: 1 },
  { id: "sheila", label: "Aussie (female)", langs: ["en-AU", "en-GB", "en"], female: true,
    phrases: { loadTruck: "Load truck.", loadComplete: "Load complete.", test: "Good morning." }, rate: 0.95, pitch: 1.05 },
  { id: "bloke", label: "Aussie (male)", langs: ["en-AU", "en-GB", "en"], female: false,
    phrases: { loadTruck: "Load truck.", loadComplete: "Load complete.", test: "Good morning." }, rate: 0.95, pitch: 0.9 },
  { id: "punjabi", label: "Punjabi (ਪੰਜਾਬੀ)", langs: ["pa-IN", "pa", "hi-IN", "hi", "en-IN"],
    phrases: {
      loadTruck: { pa: "ਵੀਰ ਜੀ, ਬਿਨ ਭਰ ਗਿਆ — ਟਰੱਕ ਲੋਡ ਕਰੋ!", hi: "वीर जी, बिन भर गया — ट्रक लोड करो!", en: "Veer ji, bin bhar gaya — truck load karo!" },
      loadComplete: { pa: "ਲੋਡ ਪੂਰਾ ਹੋ ਗਿਆ, ਟਰੱਕ ਭਰ ਗਿਆ। ਸੁਰੱਖਿਅਤ ਗੱਡੀ ਚਲਾਓ ਜੀ।", hi: "लोड पूरा हो गया, ट्रक भर गया। सुरक्षित गाड़ी चलाओ जी।", en: "Load poora ho gaya, truck bhar gaya. Surakhiat gaddi chalao ji." },
      test: { pa: "ਹੈਲੋ! ਸਤਿ ਸ੍ਰੀ ਅਕਾਲ ਜੀ, ਆਵਾਜ਼ ਚੱਲ ਰਹੀ ਹੈ।", hi: "हैलो! सत श्री अकाल जी, आवाज़ चल रही है।", en: "Hello! Sat sri akal ji, awaaz chal rahi hai." },
    }, rate: 0.95, pitch: 1 },
];
const PERSONA_KEY = "armorbatch.voice";
export const getPersonaId = () => localStorage.getItem(PERSONA_KEY) || "standard";
export const setPersonaId = (id) => localStorage.setItem(PERSONA_KEY, id);
const persona = () => VOICE_PERSONAS.find((p) => p.id === getPersonaId()) || VOICE_PERSONAS[0];

// Driver's "drive safe" call-out language, chosen at pre-start. Persisted per phone.
const DRIVE_SAFE_KEY = "armorbatch.driveSafe";
export const DRIVE_SAFE_OPTIONS = [
  { id: "aussie", label: "Aussie", sample: "Drive safe, mate." },
  { id: "punjabi", label: "Punjabi (ਪੰਜਾਬੀ)", sample: "ਸੁਰੱਖਿਅਤ ਗੱਡੀ ਚਲਾਓ ਜੀ" },
];
export const getDriveSafeId = () => localStorage.getItem(DRIVE_SAFE_KEY) || "aussie";
export const setDriveSafeId = (id) => localStorage.setItem(DRIVE_SAFE_KEY, id);
export function speakDriveSafe(id = getDriveSafeId()) {
  if (!("speechSynthesis" in window)) return;
  const p = id === "punjabi" ? VOICE_PERSONAS.find((x) => x.id === "punjabi") : VOICE_PERSONAS.find((x) => x.id === "bloke");
  const { voice, lang } = pickVoice(p);
  const l = (voice?.lang || lang || "").toLowerCase();
  const text = id === "punjabi"
    ? (l.startsWith("pa") ? "ਸੁਰੱਖਿਅਤ ਗੱਡੀ ਚਲਾਓ ਜੀ।" : l.startsWith("hi") ? "सुरक्षित गाड़ी चलाओ जी।" : "Surakhiat gaddi chalao ji.")
    : "Drive safe, mate.";
  try {
    const u = new SpeechSynthesisUtterance(text);
    if (voice) u.voice = voice;
    u.lang = voice?.lang || lang; u.rate = p.rate; u.pitch = p.pitch; u.volume = 1;
    window.speechSynthesis.speak(u);
  } catch { /* no voices */ }
}

// Plain-English call-out on the driver's phone (turn-by-turn, arrivals). Aussie male voice.
export function speakNav(text) {
  if (!("speechSynthesis" in window) || !text) return;
  try {
    const p = VOICE_PERSONAS.find((x) => x.id === "bloke");
    const { voice, lang } = pickVoice(p);
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    if (voice) u.voice = voice;
    u.lang = voice?.lang || lang; u.rate = 1; u.pitch = p.pitch; u.volume = 1;
    window.speechSynthesis.speak(u);
  } catch { /* no voices */ }
}

function pickVoice(p) {
  const voices = window.speechSynthesis?.getVoices?.() || [];
  for (const lang of p.langs) {
    let pool = voices.filter((v) => v.lang && v.lang.toLowerCase().replace("_", "-").startsWith(lang.toLowerCase()));
    if (!pool.length) continue;
    if (p.female === true) pool = pool.filter((v) => /female|emily|orla|moira|catherine|karen|zira|hazel|susan|natasha|nicole|olivia|freya|libby|isla/i.test(v.name) || !/\bmale|connor|sean|james|william|richard|george|ryan|david|mark|guy/i.test(v.name)).concat(pool);
    if (p.female === false) pool = pool.filter((v) => /\bmale|connor|sean|james|william|richard|george|ryan|david|mark|guy/i.test(v.name)).concat(pool);
    return { voice: pool[0], lang: pool[0].lang };
  }
  return { voice: null, lang: p.langs[0] };
}

function phraseFor(p, key, lang) {
  const ph = p.phrases[key];
  if (typeof ph === "string") return ph;
  const l = (lang || "").toLowerCase();
  if (l.startsWith("pa")) return ph.pa;
  if (l.startsWith("hi")) return ph.hi;
  return ph.en;
}

function say(key) {
  if (!("speechSynthesis" in window)) return;
  try {
    const p = persona();
    const { voice, lang } = pickVoice(p);
    // NO cancel() here — calling cancel() immediately before speak() silently drops the
    // utterance on Windows Chrome, which is why the plant heard nothing.
    const u = new SpeechSynthesisUtterance(phraseFor(p, key, voice?.lang || lang));
    if (voice) u.voice = voice;
    u.lang = voice?.lang || lang;
    u.rate = p.rate;
    u.pitch = p.pitch;
    u.volume = 1;
    window.speechSynthesis.speak(u);
  } catch { /* no voices installed */ }
}

// Which installed voice a persona would actually use right now (for the picker).
export function describeVoice(id) {
  const p = VOICE_PERSONAS.find((x) => x.id === id) || VOICE_PERSONAS[0];
  const { voice } = pickVoice(p);
  return voice ? `${voice.name} (${voice.lang})` : "no matching voice installed — browser default";
}
export function previewVoice(id) {
  setPersonaId(id);
  armAudio(false);
  setTimeout(() => say("test"), 150);
}

// Browsers refuse to make a sound until the page has had a real user gesture, and on
// Windows the very first utterance is dropped while the voice list loads. So we unlock
// and warm up both engines on the operator's first click anywhere on the cockpit.
let armed = false;
export const audioArmed = () => armed;
export function armAudio(test = false) {
  armed = true;
  audio();
  try {
    window.speechSynthesis?.getVoices();
    const u = new SpeechSynthesisUtterance(" ");
    u.volume = 0;
    window.speechSynthesis?.speak(u);
  } catch { /* ignore */ }
  if (test) {
    tone(880, 0.15, 0, 0.5);
    tone(1175, 0.25, 0.18, 0.5);
    setTimeout(() => say("test"), 500);
    toast.success("Plant audio armed — you'll hear Load Truck and Load Complete");
  }
}
// One-shot: the first tap anywhere arms audio silently.
export function autoArmOnFirstGesture() {
  if (armed) return () => {};
  const go = () => { armAudio(false); window.removeEventListener("pointerdown", go); window.removeEventListener("keydown", go); };
  window.addEventListener("pointerdown", go);
  window.addEventListener("keydown", go);
  return () => { window.removeEventListener("pointerdown", go); window.removeEventListener("keydown", go); };
}

// STEP 3 — the aggregate bin has reached its full target. Tell the operator to load.
export function speakLoadTruck(truckNo) {
  tone(660, 0.16, 0, 0.5);
  tone(880, 0.16, 0.18, 0.5);
  tone(1175, 0.35, 0.36, 0.5);
  say("loadTruck");
  toast.success(`LOAD TRUCK · ${truckNo || ""} — aggregate bin at target`, { duration: 10000 });
}

// Yard arrival — a truck has crossed the plant geofence coming back. Loader gets the next bin ready.
export function speakTruckBack(truckNo) {
  tone(523, 0.14, 0, 0.5);
  tone(659, 0.14, 0.16, 0.5);
  tone(784, 0.3, 0.32, 0.5);
  if ("speechSynthesis" in window) {
    try {
      const p = persona();
      const { voice, lang } = pickVoice(p);
      const u = new SpeechSynthesisUtterance(`Truck ${String(truckNo || "").replace(/-/g, " ")} is back in the yard.`);
      if (voice) u.voice = voice;
      u.lang = voice?.lang || lang; u.rate = p.rate; u.pitch = p.pitch; u.volume = 1;
      window.speechSynthesis.speak(u);
    } catch { /* no voices */ }
  }
  toast.info(`TRUCK BACK · ${truckNo || ""} — in the yard`, { duration: 10000 });
}

// STEP 6 — the aggregate bin is back to zero, the load is in the truck.
export function speakLoadComplete(truckNo) {
  tone(784, 0.18, 0, 0.5);
  tone(1046, 0.18, 0.2, 0.5);
  tone(1568, 0.5, 0.4, 0.5);
  say("loadComplete");
  toast.success(`LOAD COMPLETE · ${truckNo || ""} — bin empty, discharged to truck`, { duration: 8000 });
}

// Tracks which batches were already announced complete; the first snapshot primes
// silently so loads that finished before this screen opened don't re-fire.
export function announceReady(ref, readyBatches) {
  if (ref.current === null) { ref.current = new Set(readyBatches.map((b) => b.id)); return; }
  readyBatches.forEach((b) => { if (!ref.current.has(b.id)) { ref.current.add(b.id); speakLoadComplete(b.truck_number); } });
}

/* ── LOADER CALL-OUTS ────────────────────────────────────────────────────────
   Spoken countdown in the loader cab ("five hundred to go… two hundred… cut")
   so the operator never has to look up from the bucket. Aussie bloke voice, and
   deliberately NO speechSynthesis.cancel() — cancelling right before speak()
   silently drops the utterance on Windows Chrome. */
const LOADER_VOICE_KEY = "armorbatch.loaderVoice";
export const getLoaderVoiceOn = () => localStorage.getItem(LOADER_VOICE_KEY) !== "off";
export const setLoaderVoiceOn = (on) => localStorage.setItem(LOADER_VOICE_KEY, on ? "on" : "off");

export function speakLoader(text) {
  if (!text || !("speechSynthesis" in window) || !getLoaderVoiceOn()) return;
  try {
    const p = VOICE_PERSONAS.find((x) => x.id === "bloke") || VOICE_PERSONAS[0];
    const { voice, lang } = pickVoice(p);
    const u = new SpeechSynthesisUtterance(text);
    if (voice) u.voice = voice;
    u.lang = voice?.lang || lang;
    u.rate = 1.05;
    u.pitch = p.pitch;
    u.volume = 1;
    window.speechSynthesis.speak(u);
  } catch { /* no voices installed */ }
}
